import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, FileSpreadsheet, CheckCircle2, Package, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, KpiCard } from "@/components/KpiCard";
import { RequestDialog, SUBTYPES } from "@/components/requests/RequestDialog";
import { StatusDialog, STATUSES } from "@/components/requests/StatusDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { stationColor } from "@/lib/format";
import { sortRows } from "@/lib/sort";
import { SortSelect } from "@/components/SortSelect";

const SORTS = [["created_at:desc", "Più recenti"], ["created_at:asc", "Meno recenti"], ["type:asc", "Richiesta (A-Z)"], ["status:asc", "Stato (A-Z)"], ["station_name:asc", "Stazione (A-Z)"], ["plate:asc", "Targa (A-Z)"], ["description:asc", "Descrizione (A-Z)"]];

const PILL = { APERTA: "border-amber-200 bg-amber-50 text-amber-700", IN_LAVORAZIONE: "border-sky-200 bg-sky-50 text-sky-700", COMPLETATA: "border-emerald-200 bg-emerald-50 text-emerald-700", RIFIUTATA: "border-rose-200 bg-rose-50 text-rose-700" };

export default function RequestsPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState("all");
  const [data, setData] = useState({ items: [], summary: {} });
  const [telepass, setTelepass] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [handling, setHandling] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("open");
  const [order, setOrder] = useState("created_at:desc");

  const sid = isAdmin && stationId !== "all" ? stationId : undefined;
  const load = useCallback(() => { api.get("/requests", { params: { station_id: sid } }).then((r) => setData(r.data)); }, [sid]);
  useEffect(load, [load]);
  useEffect(() => {
    api.get("/stations").then((r) => setStations(r.data));
    api.get("/telepass").then((r) => setTelepass(r.data.items));
    api.get("/fleet/vehicles").then((r) => setVehicles(r.data.vehicles));
  }, []);

  const items = useMemo(() => {
    const t = q.trim().toLowerCase();
    return data.items.filter((i) => (filter === "all" || (filter === "open" ? ["APERTA", "IN_LAVORAZIONE"].includes(i.status) : i.status === filter))
      && (!t || [i.description, i.notes, i.telepass_number, i.plate, i.station_name, i.created_by_name, SUBTYPES[i.subtype], i.type].some((x) => (x || "").toLowerCase().includes(t))));
  }, [data.items, q, filter]);
  const sorted = useMemo(() => { const [k, d] = order.split(":"); return sortRows(items, k, d); }, [items, order]);

  const remove = async (i) => {
    if (!window.confirm("Eliminare questa richiesta?")) return;
    try { await api.delete(`/requests/${i.id}`); toast.success("Richiesta eliminata"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const exportXlsx = async () => {
    try {
      const { data: blob, headers } = await api.get("/requests/export", { params: { station_id: sid }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || "richieste.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const done = () => { setEditing(null); setHandling(null); load(); };
  const s = data.summary;
  const title = isAdmin ? (stationId === "all" ? "Richieste" : `Richieste ${stations.find((x) => x.id === stationId)?.name || ""}`) : `Richieste ${stations[0]?.name || ""}`;

  return (
    <div className="fade-up">
      <PageHeader title={title} subtitle="Richieste di materiale e Telepass dalle stazioni.">
        <Button variant="outline" onClick={exportXlsx} data-testid="request-export-button"><FileSpreadsheet className="mr-2 h-4 w-4" /> Scarica Excel</Button>
        <Button onClick={() => setEditing({ station_id: sid || "" })} data-testid="request-add-button"><Plus className="mr-2 h-4 w-4" /> Nuova richiesta</Button>
      </PageHeader>

      {isAdmin && (
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="request-station-tabs">
          {[{ id: "all", name: "Tutte le stazioni" }, ...stations].map((st) => (
            <button key={st.id} onClick={() => setStationId(st.id)} data-testid={`request-tab-${st.id}`}
              className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${stationId === st.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
              {st.id !== "all" && <span className="h-2 w-2 rounded-full" style={{ background: stationColor(stations, st.id) }} />}{st.name}
            </button>
          ))}
        </div>
      )}

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard label="Aperte" value={s.aperta ?? 0} accent="#D97706" testId="request-kpi-open" />
        <KpiCard label="In lavorazione" value={s.in_lavorazione ?? 0} accent="#0284C7" testId="request-kpi-progress" />
        <KpiCard label="Completate" value={s.completata ?? 0} accent="#059669" testId="request-kpi-done" />
        <KpiCard label="Rifiutate" value={s.rifiutata ?? 0} accent="#DC2626" testId="request-kpi-rejected" />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="inline-flex flex-wrap rounded-lg border border-slate-200 bg-white p-1" data-testid="request-filters">
          {[["open", "Da evadere"], ["COMPLETATA", "Completate"], ["RIFIUTATA", "Rifiutate"], ["all", "Tutte"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`request-filter-${k}`}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${filter === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
          ))}
        </div>
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca descrizione, targa, Telepass..." className="pl-9" data-testid="request-search-input" />
        </div>
        <SortSelect value={order} onChange={setOrder} options={SORTS} testId="request-sort-select" />
      </div>

      <div className="space-y-3" data-testid="request-list">
        {sorted.map((i) => (
          <div key={i.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`request-row-${i.id}`}>
            <div className="flex flex-wrap items-start gap-3">
              <div className={`rounded-lg p-2 ${i.type === "TELEPASS" ? "bg-violet-50 text-violet-700" : "bg-slate-100 text-slate-700"}`}>{i.type === "TELEPASS" ? <CreditCard className="h-5 w-5" /> : <Package className="h-5 w-5" />}</div>
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
                  {i.type === "TELEPASS" ? `Telepass · ${SUBTYPES[i.subtype] || ""}` : "Materiale"}{i.quantity && <span className="font-normal text-slate-500">· {i.quantity}</span>}
                  <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${PILL[i.status]}`} data-testid={`request-status-pill-${i.id}`}>{STATUSES[i.status]}</span>
                </p>
                <p className="mt-0.5 text-xs text-slate-500">{i.station_name} · {new Date(i.created_at).toLocaleDateString("it-IT")} · {i.created_by_name}
                  {i.telepass_number && <> · Telepass <b className="font-mono-num">{i.telepass_number}</b></>}{i.plate && <> · Targa <b className="font-mono-num">{i.plate}</b></>}</p>
                {i.description && <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{i.description}</p>}
                {i.notes && <p className="mt-1 whitespace-pre-wrap text-xs text-slate-500">Note: {i.notes}</p>}
                {i.admin_reply && <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid={`request-reply-${i.id}`}><b>Risposta{i.handled_by_name ? ` di ${i.handled_by_name}` : ""}:</b> {i.admin_reply}</p>}
              </div>
              <div className="flex gap-1">
                {isAdmin && <Button size="sm" variant="outline" onClick={() => setHandling(i)} data-testid={`request-handle-${i.id}`}><CheckCircle2 className="mr-1 h-4 w-4" /> Gestisci</Button>}
                {(isAdmin || !["COMPLETATA", "RIFIUTATA"].includes(i.status)) && <Button size="sm" variant="ghost" onClick={() => setEditing(i)} data-testid={`request-edit-${i.id}`}><Pencil className="h-4 w-4" /></Button>}
                {(isAdmin || i.status === "APERTA") && <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => remove(i)} data-testid={`request-delete-${i.id}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500" data-testid="request-empty">Nessuna richiesta.</p>}
      </div>
      <RequestDialog item={editing} stations={stations} telepass={telepass} vehicles={vehicles} isAdmin={isAdmin} onClose={() => setEditing(null)} onSaved={done} />
      <StatusDialog item={handling} onClose={() => setHandling(null)} onSaved={() => { done(); api.get("/telepass").then((r) => setTelepass(r.data.items)); }} />
    </div>
  );
}
